import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BookOpen,
  ClipboardList,
  Users,
  BarChart3,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Key,
  Eye,
  EyeOff,
  CheckCircle,
  Building2,
  Lightbulb,
  FolderOpen,
  Handshake,
  Tags
} from 'lucide-react';

const SystemAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/system-admin',
      icon: LayoutDashboard,
      description: 'Overview & Analytics'
    },
    {
      name: '6 Pillars CMS',
      path: '/system-admin',
      icon: BookOpen,
      description: 'Manage Learning Content',
      action: 'cms'
    },
    {
      name: 'Assessment Engine',
      path: '/assessment-management',
      icon: ClipboardList,
      description: 'Questions & Rubrics'
    },
    {
      name: 'Assessment Monitoring',
      path: '/admin/assessment-monitoring',
      icon: BarChart3,
      description: 'Track Progress & Tiers'
    },
    {
      name: 'Approval Hub',
      path: '/admin/approval-hub',
      icon: CheckCircle,
      description: 'Review Submissions'
    },
    {
      name: 'User Management',
      path: '/admin/user-management',
      icon: Users,
      description: 'Organizations & Users'
    },
    {
      name: 'Evaluator Management',
      path: '/admin/evaluator-management',
      icon: Users,
      description: 'Manage Evaluators & Assignments'
    },
    {
      name: 'Industries Master',
      path: '/admin/industries',
      icon: Building2,
      description: 'Manage Industries'
    },
    {
      name: 'Best Practices',
      path: '/admin/best-practices',
      icon: Lightbulb,
      description: 'Manage Best Practices'
    },
    {
      name: 'Directory',
      path: '/admin/directory',
      icon: FolderOpen,
      description: 'Certified Directory'
    },
    {
      name: 'Partner Management',
      path: '/admin/partners',
      icon: Handshake,
      description: 'Partner Applications'
    },
    {
      name: 'Partnership Types',
      path: '/admin/partnership-types',
      icon: Tags,
      description: 'Manage Partnership Types'
    },
    {
      name: 'Organization Types',
      path: '/admin/organization-types',
      icon: Building2,
      description: 'Manage Org Types & Fees'
    },
    {
      name: 'Question Selection',
      path: '/admin/question-selection',
      icon: ClipboardList,
      description: 'Configure Required Questions'
    },
    {
      name: 'System Config',
      path: '/system-config',
      icon: Settings,
      description: 'System Settings'
    },
    {
      name: 'Analytics',
      path: '/analytics',
      icon: TrendingUp,
      description: 'Platform Analytics'
    },
    {
      name: 'Reports',
      path: '/reports',
      icon: BarChart3,
      description: 'Generate Reports'
    },
    {
      name: 'Organization Progress',
      path: '/system-admin',
      icon: TrendingUp,
      description: 'Learning Analytics',
      action: 'orgProgress'
    }
  ];

  const isActive = (item) => {
    if (item.path === location.pathname) {
      return true;
    }
    return false;
  };

  const handleNavigation = (item) => {
    if (item.action) {
      // For System Admin Dashboard tabs
      navigate(item.path, { state: { activeView: item.action } });
    } else {
      navigate(item.path);
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.oldPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setIsChangingPassword(true);

    try {
      // Get username from session storage
      const userStr = sessionStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      const username = user?.username || 'cness_superadmin_2025';

      const response = await fetch(`${BACKEND_URL}/api/users/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username,
          old_password: passwordForm.oldPassword,
          new_password: passwordForm.newPassword,
          confirm_password: passwordForm.confirmPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setShowChangePassword(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(data.detail || 'Failed to change password');
      }
    } catch (error) {
      setPasswordError('Error changing password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar - Desktop */}
      <div
        className={`hidden lg:flex lg:flex-col bg-gradient-to-b from-indigo-900 via-purple-900 to-indigo-900 text-white transition-all duration-300 ${
          sidebarCollapsed ? 'w-20' : 'w-72'
        }`}
      >
        {/* Logo & Toggle */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-2xl font-black tracking-tight">CNESS</h1>
                <p className="text-xs text-indigo-200 mt-1">System Admin</p>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 hover:bg-white/10 rounded-lg transition-all"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <Menu className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            
            return (
              <button
                key={item.name}
                onClick={() => handleNavigation(item)}
                className={`w-full flex items-center px-3 py-3 rounded-xl transition-all duration-200 group ${
                  active
                    ? 'bg-white text-indigo-900 shadow-lg'
                    : 'text-indigo-100 hover:bg-white/10 hover:text-white'
                }`}
                title={sidebarCollapsed ? item.name : ''}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-indigo-600' : ''}`} />
                {!sidebarCollapsed && (
                  <div className="ml-3 flex-1 text-left">
                    <p className={`text-sm font-bold ${active ? 'text-indigo-900' : ''}`}>
                      {item.name}
                    </p>
                    <p className={`text-xs ${active ? 'text-indigo-600' : 'text-indigo-300'}`}>
                      {item.description}
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          {!sidebarCollapsed ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center font-bold">
                  SA
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold">System Admin</p>
                  <p className="text-xs text-indigo-300">contact@cness.co</p>
                </div>
              </div>
              <button
                onClick={() => setShowChangePassword(true)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-indigo-500/30 hover:bg-indigo-500/50 text-indigo-200 hover:text-white rounded-lg transition-all"
              >
                <Key className="w-4 h-4" />
                <span className="text-sm font-semibold">Change Password</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 hover:text-white rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-semibold">Logout</span>
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                onClick={() => setShowChangePassword(true)}
                className="w-full p-2 hover:bg-indigo-500/30 rounded-lg transition-all"
                title="Change Password"
              >
                <Key className="w-5 h-5 mx-auto text-indigo-300" />
              </button>
              <button
                onClick={handleLogout}
                className="w-full p-2 hover:bg-red-500/20 rounded-lg transition-all"
                title="Logout"
              >
                <LogOut className="w-5 h-5 mx-auto text-red-300" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
          <div
            className="fixed inset-y-0 left-0 w-72 bg-gradient-to-b from-indigo-900 via-purple-900 to-indigo-900 text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Header */}
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-black">CNESS</h1>
                <p className="text-xs text-indigo-200 mt-1">System Admin</p>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                
                return (
                  <button
                    key={item.name}
                    onClick={() => handleNavigation(item)}
                    className={`w-full flex items-center px-3 py-3 rounded-xl transition-all ${
                      active
                        ? 'bg-white text-indigo-900 shadow-lg'
                        : 'text-indigo-100 hover:bg-white/10'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${active ? 'text-indigo-600' : ''}`} />
                    <div className="ml-3 flex-1 text-left">
                      <p className={`text-sm font-bold ${active ? 'text-indigo-900' : ''}`}>
                        {item.name}
                      </p>
                      <p className={`text-xs ${active ? 'text-indigo-600' : 'text-indigo-300'}`}>
                        {item.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Mobile User Section */}
            <div className="p-4 border-t border-white/10">
              <div className="flex items-center space-x-3 p-3 bg-white/10 rounded-lg mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center font-bold">
                  SA
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold">System Admin</p>
                  <p className="text-xs text-indigo-300">contact@cness.co</p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 rounded-lg transition-all"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-semibold">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <h1 className="text-lg font-black text-gray-900">CNESS Admin</h1>
            <div className="w-10"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Key className="w-5 h-5" />
                Change Password
              </h2>
              <p className="text-indigo-100 mt-1 text-sm">Update your account password</p>
            </div>
            
            <div className="p-6 space-y-4">
              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {passwordSuccess}
                </div>
              )}

              {/* Current Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    value={passwordForm.oldPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, oldPassword: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600 transition-colors"
                  >
                    {showOldPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    placeholder="Enter new password (min 8 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600 transition-colors"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError('');
                    setPasswordSuccess('');
                    setShowOldPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAdminLayout;
